import React, { useState } from 'react';
import axios from 'axios';
import { API_URL } from './config';
import './ApiDocs.css';

function ApiDocs() {
  const [activeSection, setActiveSection] = useState('accounts');
  const [copiedEndpoint, setCopiedEndpoint] = useState(null);

  const copyToClipboard = (text, endpoint) => {
    navigator.clipboard.writeText(text);
    setCopiedEndpoint(endpoint);
    setTimeout(() => setCopiedEndpoint(null), 2000);
  };

  const endpoints = {
    accounts: {
      title: 'Accounts',
      description: 'Manage IBKR accounts linked to users',
      methods: [
        {
          method: 'GET',
          path: '/api/portfolio/accounts/',
          description: 'Get all accounts',
          params: [
            { name: 'user', type: 'integer/string', description: 'Filter by user ID or username (optional, normally auto-filtered to logged-in user)' },
            { name: 'broker_account_id', type: 'string', description: 'Filter by IBKR account ID (partial match)' },
            { name: 'search', type: 'string', description: 'Search in account ID and display name' }
          ],
          example: `${API_URL}/portfolio/accounts/`,
          exampleFiltered: `${API_URL}/portfolio/accounts/?broker_account_id=U19362604`
        },
        {
          method: 'GET',
          path: '/api/portfolio/accounts/{id}/',
          description: 'Get specific account',
          example: `${API_URL}/portfolio/accounts/1/`
        },
        {
          method: 'GET',
          path: '/api/portfolio/accounts/{id}/summary/',
          description: 'Get account summary with record counts (open positions, equity records, trades, option EAE, NAV changes)',
          example: `${API_URL}/portfolio/accounts/1/summary/`,
          responseNote: 'Returns account details plus counts for each record type'
        },
        {
          method: 'POST',
          path: '/api/portfolio/accounts/',
          description: 'Create a new account',
          example: `${API_URL}/portfolio/accounts/`
        },
        {
          method: 'PUT/PATCH',
          path: '/api/portfolio/accounts/{id}/',
          description: 'Update an account',
          example: `${API_URL}/portfolio/accounts/1/`
        },
        {
          method: 'DELETE',
          path: '/api/portfolio/accounts/{id}/',
          description: 'Delete an account',
          example: `${API_URL}/portfolio/accounts/1/`
        }
      ]
    },
    openPositions: {
      title: 'Open Positions',
      description: 'Current open positions per account (fully refreshed on each import). All monetary values are converted to EUR using fx_rate_to_base.',
      methods: [
        {
          method: 'GET',
          path: '/api/portfolio/open-positions/',
          description: 'Get all open positions',
          params: [
            { name: 'account', type: 'integer', description: 'Filter by account ID' },
            { name: 'account_id', type: 'string', description: 'Filter by IBKR account ID (e.g., U19362604)' },
            { name: 'asset_category', type: 'string', description: 'Filter by asset category (STK, OPT, etc.)' },
            { name: 'currency', type: 'string', description: 'Filter by currency (note: API returns all values in EUR)' },
            { name: 'report_date', type: 'string', description: 'Filter by report date' },
            { name: 'search', type: 'string', description: 'Search in symbol, description, conid' },
            { name: 'ordering', type: 'string', description: 'Sort by symbol, position_value, report_date' }
          ],
          example: `${API_URL}/portfolio/open-positions/`,
          exampleFiltered: `${API_URL}/portfolio/open-positions/?account_id=U19362604&asset_category=OPT`
        }
      ]
    },
    equity: {
      title: 'Equity Records',
      description: 'Aggregated historical equity data per date. All user accounts are aggregated by default, or filtered by a specific account.',
      methods: [
        {
          method: 'GET',
          path: '/api/portfolio/equity/',
          description: 'Get aggregated equity data per date (no pagination, direct array)',
          params: [
            { name: 'account_id', type: 'string', description: 'Filter by IBKR account ID (optional, if not specified all accounts are aggregated)' },
            { name: 'account', type: 'integer', description: 'Filter by account ID (alternative to account_id)' }
          ],
          example: `${API_URL}/portfolio/equity/`,
          exampleFiltered: `${API_URL}/portfolio/equity/?account_id=U19362604`,
          responseNote: 'Returns a direct array (no pagination), sorted by date (newest first). Each entry contains account_count and accounts array.'
        }
      ]
    },
    trades: {
      title: 'Trades',
      description: 'All trades per account (grows over time, no duplicates). Supports PATCH to update tags.',
      methods: [
        {
          method: 'GET',
          path: '/api/portfolio/trades/',
          description: 'Get all trades',
          params: [
            { name: 'account', type: 'integer', description: 'Filter by account ID' },
            { name: 'account_id', type: 'string', description: 'Filter by IBKR account ID (e.g., U19362604)' },
            { name: 'buy_sell', type: 'string', description: 'Filter by BUY or SELL' },
            { name: 'asset_category', type: 'string', description: 'Filter by asset category (STK, OPT, etc.)' },
            { name: 'trade_date', type: 'string', description: 'Filter by trade date' },
            { name: 'search', type: 'string', description: 'Search in trade_id, symbol, description, conid' },
            { name: 'ordering', type: 'string', description: 'Sort by trade_date' },
            { name: 'page_size', type: 'integer', description: 'Number of results per page (default: 100)' }
          ],
          example: `${API_URL}/portfolio/trades/`,
          exampleFiltered: `${API_URL}/portfolio/trades/?account_id=U19362604&buy_sell=BUY&page_size=10`
        },
        {
          method: 'PATCH',
          path: '/api/portfolio/trades/{id}/',
          description: 'Update tags for a trade (only tags can be updated, use tag_ids array)',
          example: `${API_URL}/portfolio/trades/1/`
        },
        {
          method: 'GET',
          path: '/api/portfolio/trades/rolled_options/',
          description: 'Get rolled options grouped by underlying_symbol + account. Returns groups with total P&L (realized + unrealized) and individual trades.',
          params: [],
          example: `${API_URL}/portfolio/trades/rolled_options/`,
          responseNote: 'Returns groups array with underlying_symbol, account info, total P&L, and conid_groups containing trades.'
        },
        {
          method: 'GET',
          path: '/api/portfolio/trades/covered_calls/',
          description: 'Get closed covered calls with P&L, days open, and percentage of expiry time. A covered call is a closed short call (SELL to open, BUY to close).',
          params: [
            { name: 'account_id', type: 'string', description: 'Filter by IBKR account ID' },
            { name: 'date_from', type: 'string', description: 'Filter from date (YYYY-MM-DD)' },
            { name: 'date_to', type: 'string', description: 'Filter to date (YYYY-MM-DD)' },
            { name: 'group_by', type: 'string', description: 'Group by: week, month, year, underlying_symbol, or none (default: none)' }
          ],
          example: `${API_URL}/portfolio/trades/covered_calls/`,
          exampleFiltered: `${API_URL}/portfolio/trades/covered_calls/?account_id=U19362604&group_by=month`
        }
      ]
    },
    optionEAE: {
      title: 'Option EAE',
      description: 'Option Exercise, Assignment, Expiration records',
      methods: [
        {
          method: 'GET',
          path: '/api/portfolio/option-eae/',
          description: 'Get all Option EAE records',
          params: [
            { name: 'account', type: 'integer', description: 'Filter by account ID' },
            { name: 'account_id', type: 'string', description: 'Filter by IBKR account ID (e.g., U19362604)' },
            { name: 'transaction_type', type: 'string', description: 'Filter by transaction type (Assignment, Exercise, Expiration)' },
            { name: 'date', type: 'string', description: 'Filter by date' },
            { name: 'search', type: 'string', description: 'Search in symbol, description, conid' },
            { name: 'ordering', type: 'string', description: 'Sort by date' }
          ],
          example: `${API_URL}/portfolio/option-eae/`,
          exampleFiltered: `${API_URL}/portfolio/option-eae/?account_id=U19362604&transaction_type=Expiration`
        }
      ]
    },
    changeInNAV: {
      title: 'Change in NAV',
      description: 'Deposits/withdrawals history per account',
      methods: [
        {
          method: 'GET',
          path: '/api/portfolio/change-in-nav/',
          description: 'Get all NAV changes',
          params: [
            { name: 'account', type: 'integer', description: 'Filter by account ID' },
            { name: 'account_id', type: 'string', description: 'Filter by IBKR account ID (e.g., U19362604)' },
            { name: 'ordering', type: 'string', description: 'Sort by imported_at, from_date, to_date' }
          ],
          example: `${API_URL}/portfolio/change-in-nav/`,
          exampleFiltered: `${API_URL}/portfolio/change-in-nav/?account_id=U19362604`
        }
      ]
    },
    importHistory: {
      title: 'Import History',
      description: 'History of all data imports. Note: The error_message field may contain error messages even if data was partially loaded successfully (status may be "error" but records_imported > 0).',
      methods: [
        {
          method: 'GET',
          path: '/api/portfolio/import-history/',
          description: 'Get import history',
          params: [
            { name: 'user', type: 'integer/string', description: 'Filter by user ID or username (optional, normally auto-filtered to logged-in user)' },
            { name: 'account', type: 'integer', description: 'Filter by account ID' },
            { name: 'account_id', type: 'string', description: 'Filter by IBKR account ID (e.g., U19362604)' },
            { name: 'status', type: 'string', description: 'Filter by status (success, error, partial)' },
            { name: 'import_type', type: 'string', description: 'Filter by import type' },
            { name: 'search', type: 'string', description: 'Search in file_path and account ID' },
            { name: 'ordering', type: 'string', description: 'Sort by created_at (default: newest first)' }
          ],
          example: `${API_URL}/portfolio/import-history/`,
          exampleFiltered: `${API_URL}/portfolio/import-history/?account_id=U19362604&status=success`,
          responseNote: 'Returns paginated response with count, next, previous, and results array. Each result contains: id, user, user_username, account, account_id, import_type, file_path, from_date, to_date, when_generated, records_imported, status, error_message, created_at.'
        }
      ]
    },
    userSettings: {
      title: 'User Settings',
      description: 'User settings and API keys for IBKR Flex Query integration',
      methods: [
        {
          method: 'GET',
          path: '/api/portfolio/user-settings/',
          description: 'Get user settings (automatically filtered to logged-in user)',
          params: [
            { name: 'user', type: 'integer/string', description: 'Filter by user ID or username (optional, normally auto-filtered)' },
            { name: 'search', type: 'string', description: 'Search in username' }
          ],
          example: `${API_URL}/portfolio/user-settings/`
        },
        {
          method: 'POST',
          path: '/api/portfolio/user-settings/',
          description: 'Create new user settings',
          example: `${API_URL}/portfolio/user-settings/`
        },
        {
          method: 'PUT/PATCH',
          path: '/api/portfolio/user-settings/{id}/',
          description: 'Update user settings',
          example: `${API_URL}/portfolio/user-settings/1/`
        },
        {
          method: 'DELETE',
          path: '/api/portfolio/user-settings/{id}/',
          description: 'Delete user settings',
          example: `${API_URL}/portfolio/user-settings/1/`
        }
      ]
    },
    tags: {
      title: 'Tags',
      description: 'Tags for categorizing trades and open positions. Can be global (visible to all users) or personal (only visible to creator).',
      methods: [
        {
          method: 'GET',
          path: '/api/portfolio/tags/',
          description: 'Get all tags (global tags + your personal tags)',
          params: [
            { name: 'is_global', type: 'boolean', description: 'Filter by global status' },
            { name: 'category', type: 'string', description: 'Filter by category (e.g., PMCC)' },
            { name: 'search', type: 'string', description: 'Search in name and description' }
          ],
          example: `${API_URL}/portfolio/tags/`,
          exampleFiltered: `${API_URL}/portfolio/tags/?category=PMCC`
        },
        {
          method: 'POST',
          path: '/api/portfolio/tags/',
          description: 'Create a new tag (personal by default, global only if you are staff)',
          example: `${API_URL}/portfolio/tags/`
        },
        {
          method: 'PUT/PATCH',
          path: '/api/portfolio/tags/{id}/',
          description: 'Update a tag (only your own tags, or global tags if you are staff)',
          example: `${API_URL}/portfolio/tags/1/`
        },
        {
          method: 'DELETE',
          path: '/api/portfolio/tags/{id}/',
          description: 'Delete a tag (only your own tags, or global tags if you are staff)',
          example: `${API_URL}/portfolio/tags/1/`
        },
        {
          method: 'GET',
          path: '/api/portfolio/tags/{id}/connected_trades/',
          description: 'Get list of trades connected to this tag (limited to 50 for performance)',
          example: `${API_URL}/portfolio/tags/1/connected_trades/`
        }
      ]
    },
    pmcc: {
      title: 'PMCC (Poor Man\'s Covered Call)',
      description: 'PMCC strategies linking LEAPS (long-term calls) with covered calls. Automatically creates tags and applies them to related trades.',
      methods: [
        {
          method: 'GET',
          path: '/api/portfolio/pmcc/',
          description: 'Get all PMCC strategies',
          params: [
            { name: 'account', type: 'integer', description: 'Filter by account ID' },
            { name: 'underlying_symbol', type: 'string', description: 'Filter by underlying symbol' },
            { name: 'search', type: 'string', description: 'Search in name and underlying_symbol' },
            { name: 'ordering', type: 'string', description: 'Sort by created_at, updated_at, or name' }
          ],
          example: `${API_URL}/portfolio/pmcc/`,
          exampleFiltered: `${API_URL}/portfolio/pmcc/?underlying_symbol=AAPL`
        },
        {
          method: 'POST',
          path: '/api/portfolio/pmcc/',
          description: 'Create a new PMCC strategy. Requires leaps_trade_ids (array of Trade IDs for LEAPS) and optionally covered_calls_trade_ids.',
          example: `${API_URL}/portfolio/pmcc/`
        },
        {
          method: 'GET',
          path: '/api/portfolio/pmcc/{id}/',
          description: 'Get specific PMCC strategy',
          example: `${API_URL}/portfolio/pmcc/1/`
        },
        {
          method: 'PUT/PATCH',
          path: '/api/portfolio/pmcc/{id}/',
          description: 'Update PMCC strategy (can update leaps_trade_ids, covered_calls_trade_ids, description, trade_plan, is_closed)',
          example: `${API_URL}/portfolio/pmcc/1/`
        },
        {
          method: 'DELETE',
          path: '/api/portfolio/pmcc/{id}/',
          description: 'Delete PMCC strategy',
          example: `${API_URL}/portfolio/pmcc/1/`
        },
        {
          method: 'GET',
          path: '/api/portfolio/pmcc/{id}/all_trades/',
          description: 'Get all trades (opening + closing) for all LEAPS and covered calls in this PMCC, grouped by CONID',
          example: `${API_URL}/portfolio/pmcc/1/all_trades/`,
          responseNote: 'Returns trades_by_conid object with LEAPS and covered calls CONIDs, and all related trades grouped by CONID.'
        }
      ]
    },
    userAccessGrants: {
      title: 'User Access Grants',
      description: 'Manage access grants to view other users\' data. Users can grant access to their data to other users.',
      methods: [
        {
          method: 'GET',
          path: '/api/portfolio/user-access-grants/',
          description: 'Get all access grants (both grants you gave and grants you received)',
          params: [
            { name: 'grantor', type: 'integer', description: 'Filter by grantor user ID' },
            { name: 'grantee', type: 'integer', description: 'Filter by grantee user ID' },
            { name: 'is_active', type: 'boolean', description: 'Filter by active status' }
          ],
          example: `${API_URL}/portfolio/user-access-grants/`
        },
        {
          method: 'POST',
          path: '/api/portfolio/user-access-grants/',
          description: 'Grant access to your data to another user (use grantee_username field)',
          example: `${API_URL}/portfolio/user-access-grants/`
        },
        {
          method: 'PUT/PATCH',
          path: '/api/portfolio/user-access-grants/{id}/',
          description: 'Update access grant (e.g., deactivate with is_active=false)',
          example: `${API_URL}/portfolio/user-access-grants/1/`
        },
        {
          method: 'DELETE',
          path: '/api/portfolio/user-access-grants/{id}/',
          description: 'Delete access grant',
          example: `${API_URL}/portfolio/user-access-grants/1/`
        },
        {
          method: 'GET',
          path: '/api/portfolio/user-access-grants/available_users/',
          description: 'Get list of users who have granted access to you (for switching user view)',
          example: `${API_URL}/portfolio/user-access-grants/available_users/`,
          responseNote: 'Returns array of users including yourself, with grant_id for each.'
        }
      ]
    },
    wip: {
      title: 'WIP Items',
      description: 'Work in Progress items and changelog. Global todo list and changelog shared between all users.',
      methods: [
        {
          method: 'GET',
          path: '/api/portfolio/wip/',
          description: 'Get all WIP items (all users can see all items)',
          params: [
            { name: 'is_completed', type: 'boolean', description: 'Filter by completion status' },
            { name: 'changelog', type: 'boolean', description: 'Filter by changelog status (true for changelog items, false for todo items)' },
            { name: 'priority', type: 'string', description: 'Filter by priority (high, medium, low)' },
            { name: 'search', type: 'string', description: 'Search in title and description' }
          ],
          example: `${API_URL}/portfolio/wip/`,
          exampleFiltered: `${API_URL}/portfolio/wip/?changelog=false&priority=high`
        },
        {
          method: 'POST',
          path: '/api/portfolio/wip/',
          description: 'Create a new WIP item',
          example: `${API_URL}/portfolio/wip/`
        },
        {
          method: 'PUT/PATCH',
          path: '/api/portfolio/wip/{id}/',
          description: 'Update WIP item (changelog items cannot be updated, only deleted)',
          example: `${API_URL}/portfolio/wip/1/`
        },
        {
          method: 'DELETE',
          path: '/api/portfolio/wip/{id}/',
          description: 'Delete WIP item (changelog items can be deleted)',
          example: `${API_URL}/portfolio/wip/1/`
        }
      ]
    },
    data: {
      title: 'Imported Data',
      description: 'Legacy endpoint for imported data (from data_import app)',
      methods: [
        {
          method: 'GET',
          path: '/api/data/',
          description: 'Get all imported data',
          params: [],
          example: `${API_URL.replace('/portfolio', '')}/data/`
        },
        {
          method: 'GET',
          path: '/api/data/{id}/',
          description: 'Get specific imported data',
          example: `${API_URL.replace('/portfolio', '')}/data/1/`
        },
        {
          method: 'POST',
          path: '/api/data/',
          description: 'Create new imported data',
          example: `${API_URL.replace('/portfolio', '')}/data/`
        },
        {
          method: 'PUT/PATCH',
          path: '/api/data/{id}/',
          description: 'Update imported data',
          example: `${API_URL.replace('/portfolio', '')}/data/1/`
        },
        {
          method: 'DELETE',
          path: '/api/data/{id}/',
          description: 'Delete imported data',
          example: `${API_URL.replace('/portfolio', '')}/data/1/`
        },
        {
          method: 'GET',
          path: '/api/data/search/',
          description: 'Search imported data',
          params: [
            { name: 'q', type: 'string', description: 'Search query' }
          ],
          example: `${API_URL.replace('/portfolio', '')}/data/search/?q=searchterm`
        }
      ]
    },
    switchUser: {
      title: 'Switch User',
      description: 'Switch to viewing another user\'s data (requires access grant)',
      methods: [
        {
          method: 'POST',
          path: '/api/portfolio/switch-user/',
          description: 'Switch to viewing another user\'s data. Requires username in request body. Access must be granted via user-access-grants.',
          params: [],
          example: `${API_URL}/portfolio/switch-user/`,
          responseNote: 'Returns success status and viewing_as user info. To switch back, call with your own username.'
        },
        {
          method: 'GET',
          path: '/api/portfolio/current-viewing-as/',
          description: 'Get the current viewing_as user (if any)',
          params: [],
          example: `${API_URL}/portfolio/current-viewing-as/`,
          responseNote: 'Returns viewing_as user info or null if viewing your own data.'
        }
      ]
    }
  };

  const sections = Object.keys(endpoints);

  return (
    <div className="api-docs">
      <div className="api-docs-container">
        <nav className="api-docs-nav">
          <h3 style={{margin: '0 0 15px 0', color: '#333', fontSize: '1.1rem'}}>Endpoints</h3>
          {sections.map(section => (
            <button
              key={section}
              className={`nav-button ${activeSection === section ? 'active' : ''}`}
              onClick={() => setActiveSection(section)}
            >
              {endpoints[section].title}
            </button>
          ))}
        </nav>

        <main className="api-docs-content">
          {sections.map(section => {
            if (activeSection !== section) return null;
            const endpoint = endpoints[section];
            
            return (
              <div key={section} className="endpoint-section">
                <h2>{endpoint.title}</h2>
                <p className="endpoint-description">{endpoint.description}</p>

                {endpoint.methods.map((method, idx) => (
                  <div key={idx} className="method-card">
                    <div className="method-header">
                      <span className={`method-badge method-${method.method.toLowerCase()}`}>
                        {method.method}
                      </span>
                      <code className="method-path">{method.path}</code>
                    </div>
                    
                    <p className="method-description">{method.description}</p>

                    {method.params && method.params.length > 0 && (
                      <div className="params-section">
                        <h4>Query Parameters:</h4>
                        <table className="params-table">
                          <thead>
                            <tr>
                              <th>Parameter</th>
                              <th>Type</th>
                              <th>Description</th>
                            </tr>
                          </thead>
                          <tbody>
                            {method.params.map((param, pIdx) => (
                              <tr key={pIdx}>
                                <td><code>{param.name}</code></td>
                                <td>{param.type}</td>
                                <td>{param.description}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    <div className="examples-section">
                      <h4>Examples:</h4>
                      <div className="example-box">
                        <div className="example-header">
                          <span>Basic endpoint</span>
                          <button
                            className="copy-btn"
                            onClick={() => copyToClipboard(method.example, `${section}-${idx}-basic`)}
                          >
                            {copiedEndpoint === `${section}-${idx}-basic` ? '✓ Copied' : '📋 Copy'}
                          </button>
                        </div>
                        <code className="example-code">{method.example}</code>
                      </div>

                      {method.exampleFiltered && (
                        <div className="example-box">
                          <div className="example-header">
                            <span>With filters</span>
                            <button
                              className="copy-btn"
                              onClick={() => copyToClipboard(method.exampleFiltered, `${section}-${idx}-filtered`)}
                            >
                              {copiedEndpoint === `${section}-${idx}-filtered` ? '✓ Copied' : '📋 Copy'}
                            </button>
                          </div>
                          <code className="example-code">{method.exampleFiltered}</code>
                        </div>
                      )}

                      {method.responseNote && (
                        <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px', fontSize: '0.9rem' }}>
                          <strong>Response Format:</strong> {method.responseNote}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </main>
      </div>

      <div className="api-docs-footer">
        <h3>General Information</h3>
        <div className="info-grid">
          <div className="info-card">
            <h4>Authentication</h4>
            <p>
              Endpoints require an authenticated Django user. Preferred: obtain a token and send it on every request:
            </p>
            <p>
              <strong>Obtain token (password once):</strong> <code>POST {API_URL}/auth/token/</code> with JSON{' '}
              <code>{`{"username":"…","password":"…"}`}</code> — response{' '}
              <code>{`{"token":"…"}`}</code>.
            </p>
            <p>
              <strong>Or when already logged in (session / Basic / existing token):</strong> <code>GET {API_URL}/portfolio/auth/token/</code> returns the same token (creates it if needed).{' '}
              <code>POST {API_URL}/portfolio/auth/token/regenerate/</code> rotates the token and returns the new value.
            </p>
            <p>
              <strong>Use token:</strong> header <code>Authorization: Token &lt;token&gt;</code>
            </p>
            <p style={{ marginTop: '10px', fontSize: '0.9rem' }}>
              HTTP Basic authentication (same username/password) and session cookies (e.g. browsable API) still work.
            </p>
          </div>
          <div className="info-card">
            <h4>Pagination</h4>
            <p>All list endpoints support pagination:</p>
            <ul>
              <li><code>page</code> - Page number</li>
              <li><code>page_size</code> - Number of results per page (default: 100)</li>
              <li><code>page_size=all</code> - Get all data without pagination (no count/next/results, just an array)</li>
            </ul>
            <p><strong>Example:</strong></p>
            <code>{`${API_URL}/portfolio/trades/?page_size=all`}</code>
          </div>
          <div className="info-card">
            <h4>Response Format</h4>
            <p>Most list endpoints return paginated JSON:</p>
            <pre>{`{
  "count": 287,
  "next": "http://...",
  "previous": null,
  "results": [...]
}`}</pre>
            <p style={{marginTop: '10px'}}><strong>Example (import-history):</strong></p>
            <pre>{`{
  "count": 76,
  "next": null,
  "previous": null,
  "results": [
    {
      "id": 150,
      "user": 1,
      "user_username": "patrick",
      "account": 2,
      "account_id": "U19362604",
      "import_type": "ibkr_json",
      "file_path": "data/...",
      "from_date": "02/01/26",
      "to_date": "02/01/26",
      "when_generated": "",
      "records_imported": 36,
      "status": "error",
      "error_message": "Error message here",
      "created_at": "2026-01-05T06:10:06.801295+01:00"
    }
  ]
}`}</pre>
            <p style={{marginTop: '10px', fontSize: '0.9rem', fontStyle: 'italic'}}>
              <strong>Note:</strong> The error_message field may contain errors even if data was partially loaded (records_imported > 0).
            </p>
          </div>
          <div className="info-card">
            <h4>Filtering & Searching</h4>
            <p>Most endpoints support:</p>
            <ul>
              <li>Filtering on specific fields</li>
              <li>Searching in relevant text fields</li>
              <li>Sorting on various fields</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ApiDocs;

